import {
  HttpStatusCode,
  type HttpClient
} from "@/core/services/protocols/types";
import {
  IFinancasGateway,
  IGerenteContas,
  ISolicitacoesEmAberto
} from "./FinancasGateway";
import {
  IContasFinancas,
  IDREApi,
  IDREList,
  IEmpresasFinancas,
  IEmpresasCredenciadasParam,
  IEmpresasCredenciadas,
  IFinancasAuthResponse,
  IFluxoDeCaixa,
  IParmsFluxoDeCaixa,
  TDREApi,
  IParmsPMR,
  IPRMResponse,
  ICentroDeCusto,
  IUserProfileResponse,
  IUserRolesResponse,
  IFinancasDefaultResponse,
  ISolicitacoesEmAbertoResponse,
  TAdquirente,
  IresponseSolicitarCredenciamento,
  ICreateCredenciamentoPDV,
  TAPdvId,
  IPdvId,
  IEmpresasPDV,
  IEmpresasPDVResponse,
  IListPDV,
  IintegracoesPorPDV,
  IAdquirentesList,
  IAtualizarEtapa,
  ICieloSolicitarLinkReturn,
  ICreateCredenciamento,
  ICreateTaxas,
  ICredenciamentoEmpresasTable,
  ICredenciamentoId,
  IObterLinkCielo,
  IReturnAdquirentesList,
  IReturnAtualizarEtapa,
  IReturnEmpresasListByAdquirente,
  IReturnTableEmpresas,
  ISolicitarCredenciamento,
  IStatusTaxasReturn,
  ITaxaId,
  TAdquirentId,
  IParamsTreinamento,
  IReturnTreinamento,
  IParamsConfirmarAgendamento,
  IPlanosOrcamentariosFinancas
} from "./types";
import { UserProfile } from "./UserProfile";
import { UserRoles } from "./UserRoles";
import { injectable, inject, optional } from "inversify";
import { DITypes } from "@/core/@types/types";
import { IStatusPrimeiosPassos } from "@/features/financas/widgets/credenciamento/componentes/FluxosCredenciamento/shared/type";
import {
  FinancasDevHttpDi,
  type IFinancasDevHttp
} from "@/core/services/FinancasDevHttp";
import "reflect-metadata";

@injectable()
export default class FinancasHttpGateway implements IFinancasGateway {
  @inject(DITypes.FINANCAS_HTTP_CLIENT)
  @optional()
  private httpClient?: HttpClient;

  @inject(DITypes.FINANCAS_API_HTTP_CLIENT)
  @optional()
  private httpClientFinancasApi?: HttpClient;

  @inject(UserProfile)
  private userProfile!: UserProfile;

  @inject(UserRoles)
  private userRoles!: UserRoles;

  @inject(FinancasDevHttpDi)
  private financasDevHttp!: IFinancasDevHttp;

  async confirmarAgendamento(
    params: IParamsConfirmarAgendamento
  ): Promise<boolean> {
    const response = await this.httpClient?.post<IReturnTreinamento>({
      url: "TreinamentoAPI/ConfirmarAgendamento",
      body: {
        ...params
      }
    });

    if (!response?.body) return false;

    return true;
  }

  async listasTreinamento(): Promise<IReturnTreinamento> {
    const response = await this.httpClient?.get<IReturnTreinamento>({
      url: "TreinamentoAPI/BuscarTreinamentos"
    });

    if (!response?.body) return {};

    return response?.body;
  }

  async agendarReuniao(uri: string): Promise<boolean> {
    const response = await this.httpClient?.post<IReturnTreinamento>({
      url: "UsuarioAPI/ConfirmarAgendamentoKickOff",
      body: {
        uriCalendly: uri
      }
    });

    if (response?.statusCode != HttpStatusCode.success) return false;

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async mudarStatusTreinamento(params: IParamsTreinamento): Promise<any> {
    const response = await this.httpClient?.post<IReturnTreinamento>({
      url: "TreinamentoAPI/DefineTreinamento",
      body: { ...params }
    });

    if (!response?.body) return {};

    return response?.body;
  }

  async listarBoasVindas() {
    if (import.meta.env.DEV) {
      return {};
    }

    const response = await this.httpClient?.get<
      IStatusPrimeiosPassos | Record<string, boolean>
    >({
      url: "/UsuarioAPI/BuscarBoasVindas"
    });

    if (!response?.body) return {};

    return response?.body;
  }

  async ObterLinkCielo(params: IObterLinkCielo) {
    const response = await this.httpClientFinancasApi?.get<
      ICieloSolicitarLinkReturn | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/ObterUrlCielo",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async AtualizarRede(
    params: ICredenciamentoId
  ): Promise<IresponseSolicitarCredenciamento | undefined> {
    const response = await this.httpClientFinancasApi?.post<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/AtualizarRede",
      body: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async SalvarTaxa(
    params: ICreateTaxas
  ): Promise<IStatusTaxasReturn | undefined> {
    const response = await this.httpClientFinancasApi?.post<
      IStatusTaxasReturn | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/SalvarTaxaV2",
      body: { ...params }
    });

    return response?.body;
  }

  async atualizarEtapa(
    params: IAtualizarEtapa
  ): Promise<IReturnAtualizarEtapa | undefined> {
    const response = await this.httpClientFinancasApi?.post<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/AtualizarEtapa",
      body: { ...params }
    });

    return response?.body;
  }

  async DeleteTaxa(params: ITaxaId): Promise<void> {
    await this.httpClientFinancasApi?.post<IresponseSolicitarCredenciamento | undefined>({
      url: "/v1/CredenciamentoAdquirentesAPI/DeleteTaxa",
      body: { ...params }
    });
  }

  async getCredenciamento(
    params: ICredenciamentoId
  ): Promise<IresponseSolicitarCredenciamento | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/GetCredenciamento",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async solicitarCredenciamento(
    params: ISolicitarCredenciamento
  ): Promise<IresponseSolicitarCredenciamento | undefined> {
    const response = await this.httpClientFinancasApi?.post<
      IresponseSolicitarCredenciamento | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/SolicitarCredenciamento",
      body: { ...params }
    });

    return response?.body;
  }

  async getAdquirentesBandeirasList(): Promise<TAdquirente | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      TAdquirente | undefined
    >({
      url: "/v1/EnumListAPI/ListBandeiraDeCartao"
    });

    if (!response?.body) return;

    return response?.body;
  }

  async deletarCredenciamento(
    params: ICredenciamentoId
  ): Promise<string[] | undefined> {
    const response = await this.httpClientFinancasApi?.post<
      IFinancasDefaultResponse<string[]> | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/DeletarCredenciamento",
      body: { ...params }
    });

    if (!response?.body) return;

    return response?.body.Result;
  }

  async getEmpresasTable(
    params: ICredenciamentoEmpresasTable
  ): Promise<IReturnTableEmpresas | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      IReturnTableEmpresas | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/GetAdquirentesByEmpresa",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async createCredenciamento(
    params: ICreateCredenciamento
  ): Promise<IFinancasDefaultResponse<TAdquirentId> | undefined> {
    const response = await this.httpClientFinancasApi?.post<
      IFinancasDefaultResponse<TAdquirentId> | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/CreateCredenciamento",
      body: { ...params }
    });

    return response?.body;
  }

  async getEmpresasByAdquirente(
    params: TAdquirentId
  ): Promise<IReturnEmpresasListByAdquirente | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      IReturnEmpresasListByAdquirente | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/GetEmpresasByAdquirente",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async getAdquirentesList(
    params: IAdquirentesList
  ): Promise<IReturnAdquirentesList | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      IReturnAdquirentesList | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/GetAdquirentes",
      queryParams: { ...params }
    });

    if (!response?.body) return;

    return response?.body;
  }

  async getUrlLoginEduca(): Promise<string | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<string | undefined>
    >({
      url: "/DashboardAPI/ObterUrlDeLoginF360Educa/"
    });

    if (!response?.body?.Ok) return;

    return response?.body.Result;
  }

  async getZendeskToken(): Promise<string | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<string> | undefined
    >({
      url: "/UsuarioAPI/GetZendeskToken/"
    });

    return response?.body?.Result;
  }

  async getCentralDeAjudaUrl(returnTo?: string): Promise<string | undefined> {
    const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";

    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<string> | undefined
    >({
      url: `/UsuarioAPI/CentralDeAjuda/${query}`
    });

    if (!response?.body?.Ok) return;

    return response?.body?.Result;
  }

  async getQuantidadeSolicitacoesEmAberto(): Promise<number | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<number> | undefined
    >({
      url: "/UsuarioAPI/QuantidadeSolicitacoesEmAberto/"
    });

    if (!response?.body?.Ok) return;

    return response?.body?.Result;
  }

  async getSolicitacoesEmAberto(): Promise<ISolicitacoesEmAberto | undefined> {
    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<ISolicitacoesEmAbertoResponse> | undefined
    >({
      url: "/UsuarioAPI/SolicitacoesEmAberto/"
    });

    const result = response?.body?.Result;

    if (!response?.body?.Ok || !result) return;

    return {
      exibirSolicitante: !!result.ExibirSolicitante,
      solicitacoes: (result.Solicitacoes || []).map((item) => ({
        numero: item.Numero,
        assunto: item.Assunto,
        descricao: item.Descricao,
        dataAbertura: item.DataAbertura,
        status: item.Status,
        statusDescricao: item.StatusDescricao,
        aguardandoFornecedor: !!item.AguardandoFornecedor,
        previsaoResolucao: item.PrevisaoResolucao,
        abertoPor: item.AbertoPor,
        url: item.Url
      }))
    };
  }

  async getGerenteDeContas(): Promise<IGerenteContas | undefined> {
    const response = await this.httpClient?.get<
      | IFinancasDefaultResponse<{
          Nome: string;
          Cargo: string;
          Email: string;
          WhatsApp: string;
        }>
      | undefined
    >({
      url: "/UsuarioAPI/GerenteDeContas/"
    });

    const result = response?.body?.Result;

    if (!response?.body?.Ok || !result?.Nome) return;

    return {
      nome: result.Nome,
      cargo: result.Cargo,
      email: result.Email,
      whatsapp: result.WhatsApp
    };
  }

  async getUserRoles(): Promise<UserRoles | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/UsuarioAPI/GetUserRoles/"
      );

      this.userRoles.response = response.Result;

      return this.userRoles;
    }

    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<IUserRolesResponse> | undefined
    >({
      url: "/UsuarioAPI/GetUserRoles/"
    });

    this.userRoles.response = response?.body?.Result;

    return this.userRoles;
  }

  async getUserProfile(): Promise<UserProfile | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/UsuarioAPI/GetCurrent/"
      );

      this.userProfile.response = response.Result;

      return this.userProfile;
    }

    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<IUserProfileResponse> | undefined
    >({
      url: "/UsuarioAPI/GetCurrent/"
    });

    this.userProfile.response = response?.body?.Result;

    return this.userProfile;
  }

  async obterCentroDeCusto(): Promise<ICentroDeCusto | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/v1/CentroDeCustoAPI/List/"
      );

      return {
        Data: response.Data,
        Total: response.Total
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      ICentroDeCusto | undefined
    >({
      url: "/v1/CentroDeCustoAPI/List/"
    });

    return response?.body;
  }

  async getObterLinhasDaDRE(id: string): Promise<TDREApi | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/DREAPI/ObterLinhasDaDRE/?Id=${id}`
      );

      return {
        Result: response.Result,
        Ok: response.Ok || false
      };
    }

    const response = await this.httpClientFinancasApi?.get<TDREApi | undefined>(
      {
        url: "/v1/DREAPI/ObterLinhasDaDRE/",
        queryParams: { Id: id }
      }
    );

    return response?.body;
  }

  async auth(): Promise<IFinancasAuthResponse | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get("/api/v2/auth");

      return response as unknown as IFinancasAuthResponse;
    }

    const response = await this.httpClient?.get<
      IFinancasAuthResponse | undefined
    >({
      url: "v2/auth"
    });

    return response?.body;
  }

  async fluxoDeCaixa(
    params: IParmsFluxoDeCaixa
  ): Promise<IFluxoDeCaixa | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/v1/FluxoDeCaixaAPI/IniciarFluxoDeCaixa",
        params
      );

      return {
        Result: response.Result,
        Ok: response.Ok || false
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IFluxoDeCaixa | undefined
    >({
      url: "/v1/FluxoDeCaixaAPI/IniciarFluxoDeCaixa",
      queryParams: params
    });

    return response?.body;
  }

  async getAccounts(
    companyIds: string[] = []
  ): Promise<IContasFinancas | undefined> {
    // Sem filtro usa ObterContasMin; com filtro, ObterContasPorEmpresasMin,
    // que espera os ids em "empresasIds" separados por vírgula.
    const url = companyIds.length
      ? "/v1/ContaAPI/ObterContasPorEmpresasMin/"
      : "/v1/ContaAPI/ObterContasMin/";
    const queryParams = companyIds.length
      ? { empresasIds: companyIds.join(",") }
      : undefined;

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(`/api${url}`, queryParams);

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClientFinancasApi?.get<IContasFinancas | undefined>({
      url,
      queryParams
    });

    return response?.body;
  }

  async getAccountNames(ids: string[]): Promise<string[] | undefined> {
    if (!ids.length) return [];

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/ContaAPI/ObterNomeDasContas/",
        { ids: ids.join(",") }
      );

      return response.Result;
    }

    const response = await this.httpClient?.get<
      IFinancasDefaultResponse<string[]> | undefined
    >({
      url: "/ContaAPI/ObterNomeDasContas/",
      queryParams: { ids: ids.join(",") }
    });

    return response?.body?.Result;
  }

  async getPlanosOrcamentarios(): Promise<IPlanosOrcamentariosFinancas | []> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/PlanoOrcamentarioAPI/ObterPlanosOrcamentariosMin/"
      );

      return {
        Data: response.Data,
        Total: response.Total || 0
      };
    }

    const response = await this.httpClient?.get<
      IPlanosOrcamentariosFinancas | []
    >({
      url: "/PlanoOrcamentarioAPI/ObterPlanosOrcamentariosMin/"
    });

    if (response?.statusCode !== 200 || !response?.body) return [];

    return response.body;
  }

  async getEmpresas(): Promise<IEmpresasFinancas | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        "/api/EmpresaAPI/ObterEmpresasMin/"
      );

      return {
        Data: response.Data
      };
    }

    const response = await this.httpClient?.get<IEmpresasFinancas | undefined>({
      url: "/EmpresaAPI/ObterEmpresasMin/"
    });

    return response?.body;
  }

  async getEmpresasCredenciadas(
    params: IEmpresasCredenciadasParam
  ): Promise<IEmpresasCredenciadas | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      IEmpresasCredenciadas | undefined
    >({
      url: "/v1/CredenciamentoAdquirentesAPI/GetAllEmpresasCredenciadas",
      queryParams: params
    });

    return response?.body;
  }

  async getDreList(): Promise<IDREList | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/DREAPI/List?sort=&group=&filter=`
      );

      return {
        Data: response.Data
      };
    }

    const response = await this.httpClientFinancasApi?.get<
      IDREList | undefined
    >({
      url: "/v1/DREAPI/List?sort=&group=&filter="
    });

    return response?.body;
  }

  async getPmrPagarReceber(
    params: IParmsPMR
  ): Promise<IPRMResponse | undefined> {
    const response = await this.httpClient?.get<IPRMResponse | undefined>({
      url: "/PlanoOrcamentarioAPI/PrazoMedioDeRecebimento/",
      queryParams: params
    });

    return response?.body;
  }

  async getDreApi(params: IDREApi): Promise<TDREApi | undefined> {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.post(
        `/api/v1/DREAPI/ListDRE/`,
        {
          ...params
        }
      );

      return {
        Result: response.Result,
        Ok: response.Ok || false
      };
    }

    const response = await this.httpClientFinancasApi?.post<
      TDREApi | undefined
    >({
      url: "/v1/DREAPI/ListDRE/",
      body: { ...params }
    });

    return response?.body;
  }

  async getListOperadoraDeCartao(): Promise<TAdquirente | undefined> {
    const response = await this.httpClientFinancasApi?.get<
      TAdquirente | undefined
    >({
      url: "/v1/EnumListAPI/ListOperadoraDeCartao/"
    });

    return response?.body;
  }

  // credenciamento PDV
  async createCredenciamentoPDV(
    params: ICreateCredenciamentoPDV
  ): Promise<IFinancasDefaultResponse<TAPdvId> | undefined> {
    const response = await this.httpClient?.post<
      IFinancasDefaultResponse<TAPdvId> | undefined
    >({
      url: "/IntegracoesPDVsApi/AdicionarIntegracaoPDVDaEmpresa",
      body: { ...params }
    });

    return response?.body;
  }

  async getListPDVPaginado(
    params: IEmpresasPDV
  ): Promise<IEmpresasPDVResponse | undefined> {
    const response = await this.httpClient?.get<
      IEmpresasPDVResponse | undefined
    >({
      url: "/IntegracoesPDVsApi/ObterIntegracoesPDVsDasEmpresas",
      queryParams: params
    });

    return response?.body;
  }

  async getListPDVCriados(): Promise<IListPDV[] | undefined> {
    const response = await this.httpClient?.get<IListPDV[] | undefined>({
      url: "/IntegracoesPDVsApi/ListarPDVsDeEmpresaCriados"
    });

    return response?.body;
  }

  async getListPDV(): Promise<IListPDV[] | undefined> {
    const response = await this.httpClient?.get<IListPDV[] | undefined>({
      url: "/IntegracoesPDVsApi/ListarIntegracoesPdvDaEmpresa"
    });

    return response?.body;
  }

  async DeletePDV(params: IPdvId): Promise<void> {
    await this.httpClient?.post<IresponseSolicitarCredenciamento | undefined>({
      url: "/IntegracoesPDVsApi/RemoverIntegracaoPDVDaEmpresa",
      body: { ...params }
    });
  }

  async DeleteIntegracoesPorPDV(params: IintegracoesPorPDV): Promise<void> {
    await this.httpClient?.post<IresponseSolicitarCredenciamento | undefined>({
      url: "/IntegracoesPDVsApi/RemoverIntegracoesPorPDV",
      body: { ...params }
    });
  }
}
